import { useEffect, useRef, useState } from "react";
import { useAuth } from "@clerk/react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { Eraser, PenLine, Type, Undo2 } from "lucide-react";

const TYPED_FONTS = [
  { id: "cursive", label: "Cursive", css: "'Brush Script MT', 'Lucida Handwriting', cursive" },
  { id: "italic", label: "Italic Serif", css: "'Times New Roman', serif" },
  { id: "script", label: "Script", css: "'Segoe Script', 'Comic Sans MS', cursive" },
];

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  approvalId: number;
  defaultName?: string;
  onSigned: () => void;
}

export function SignInAppDialog({ open, onOpenChange, approvalId, defaultName, onSigned }: Props) {
  const { getToken } = useAuth();
  const { toast } = useToast();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [tab, setTab] = useState<"draw" | "type">("draw");
  const [typedName, setTypedName] = useState(defaultName ?? "");
  const [typedFont, setTypedFont] = useState(TYPED_FONTS[0].id);
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [strokeCount, setStrokeCount] = useState(0);
  const drawing = useRef(false);
  const strokesRef = useRef<Array<Array<{ x: number; y: number }>>>([]);
  const currentStroke = useRef<Array<{ x: number; y: number }>>([]);

  useEffect(() => {
    if (open) {
      setTab("draw");
      setTypedName(defaultName ?? "");
      setTypedFont(TYPED_FONTS[0].id);
      setConsent(false);
      strokesRef.current = [];
      currentStroke.current = [];
      setStrokeCount(0);
      requestAnimationFrame(() => redrawStrokes());
    }
  }, [open, defaultName]);

  function redrawStrokes() {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    if (!ctx) return;
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, c.width, c.height);
    ctx.strokeStyle = "#0a0a0a";
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    for (const stroke of strokesRef.current) {
      if (stroke.length === 0) continue;
      ctx.beginPath();
      ctx.moveTo(stroke[0].x, stroke[0].y);
      for (let i = 1; i < stroke.length; i++) ctx.lineTo(stroke[i].x, stroke[i].y);
      ctx.stroke();
    }
  }

  function clearCanvas() {
    strokesRef.current = [];
    currentStroke.current = [];
    setStrokeCount(0);
    redrawStrokes();
  }

  function undoStroke() {
    if (strokesRef.current.length === 0) return;
    strokesRef.current.pop();
    setStrokeCount(strokesRef.current.length);
    redrawStrokes();
  }

  function getPos(e: React.PointerEvent<HTMLCanvasElement>) {
    const c = canvasRef.current!;
    const rect = c.getBoundingClientRect();
    return { x: ((e.clientX - rect.left) / rect.width) * c.width, y: ((e.clientY - rect.top) / rect.height) * c.height };
  }

  function onPointerDown(e: React.PointerEvent<HTMLCanvasElement>) {
    e.preventDefault();
    drawing.current = true;
    const { x, y } = getPos(e);
    currentStroke.current = [{ x, y }];
    const ctx = canvasRef.current!.getContext("2d")!;
    ctx.strokeStyle = "#0a0a0a";
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.beginPath();
    ctx.moveTo(x, y);
    canvasRef.current!.setPointerCapture(e.pointerId);
  }
  function onPointerMove(e: React.PointerEvent<HTMLCanvasElement>) {
    if (!drawing.current) return;
    const { x, y } = getPos(e);
    currentStroke.current.push({ x, y });
    const ctx = canvasRef.current!.getContext("2d")!;
    ctx.lineTo(x, y);
    ctx.stroke();
  }
  function onPointerUp() {
    if (drawing.current && currentStroke.current.length > 0) {
      strokesRef.current.push(currentStroke.current);
      currentStroke.current = [];
      setStrokeCount(strokesRef.current.length);
    }
    drawing.current = false;
  }

  const hasInk = strokeCount > 0;
  const canSubmit =
    consent &&
    !submitting &&
    (tab === "draw" ? hasInk : typedName.trim().length > 0);

  function renderTypedToCanvas(): string {
    const w = 600, h = 200;
    const off = document.createElement("canvas");
    off.width = w; off.height = h;
    const ctx = off.getContext("2d")!;
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, w, h);
    ctx.fillStyle = "#0a0a0a";
    const fontCss = TYPED_FONTS.find((f) => f.id === typedFont)?.css ?? "cursive";
    ctx.font = `48px ${fontCss}`;
    ctx.textBaseline = "middle";
    ctx.textAlign = "center";
    ctx.fillText(typedName.trim(), w / 2, h / 2);
    return off.toDataURL("image/png");
  }

  async function handleSubmit() {
    if (!consent) {
      toast({ title: "Consent required", description: "Please confirm you intend to sign electronically.", variant: "destructive" });
      return;
    }
    let dataUrl = "";
    if (tab === "draw") {
      if (!hasInk || !canvasRef.current) {
        toast({ title: "Signature required", description: "Please draw your signature.", variant: "destructive" });
        return;
      }
      dataUrl = canvasRef.current.toDataURL("image/png");
    } else {
      if (!typedName.trim()) {
        toast({ title: "Name required", description: "Please type your full legal name.", variant: "destructive" });
        return;
      }
      dataUrl = renderTypedToCanvas();
    }

    setSubmitting(true);
    try {
      const token = await getToken();
      const res = await fetch(`/api/approvals/${approvalId}/signature/sign-in-app`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          method: tab === "draw" ? "in_app_draw" : "in_app_typed",
          imageBase64: dataUrl,
          typedName: tab === "type" ? typedName.trim() : undefined,
          typedFont: tab === "type" ? typedFont : undefined,
          consent: true,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error((data as { error?: string }).error ?? `Error ${res.status}`);
      toast({ title: "Signed", description: "Your signature has been recorded." });
      onOpenChange(false);
      onSigned();
    } catch (err) {
      toast({ title: "Sign failed", description: err instanceof Error ? err.message : "Error", variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Sign in app</DialogTitle>
          <DialogDescription>
            Choose Draw or Type. By signing, you agree this signature is legally binding.
          </DialogDescription>
        </DialogHeader>

        <Tabs value={tab} onValueChange={(v) => setTab(v as "draw" | "type")}>
          <TabsList className="grid grid-cols-2">
            <TabsTrigger value="draw"><PenLine className="h-4 w-4 mr-1" /> Draw</TabsTrigger>
            <TabsTrigger value="type"><Type className="h-4 w-4 mr-1" /> Type</TabsTrigger>
          </TabsList>
          <TabsContent value="draw" className="space-y-2">
            <div className="border rounded-md bg-white">
              <canvas
                ref={canvasRef}
                width={600}
                height={200}
                className="w-full h-[200px] touch-none cursor-crosshair"
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerLeave={onPointerUp}
              />
            </div>
            <div className="flex justify-between items-center">
              <p className="text-xs text-muted-foreground">Draw your signature with mouse, finger, or stylus.</p>
              <div className="flex gap-1">
                <Button type="button" variant="ghost" size="sm" onClick={undoStroke} disabled={strokeCount === 0}>
                  <Undo2 className="h-3.5 w-3.5 mr-1" /> Undo
                </Button>
                <Button type="button" variant="ghost" size="sm" onClick={clearCanvas} disabled={strokeCount === 0}>
                  <Eraser className="h-3.5 w-3.5 mr-1" /> Clear
                </Button>
              </div>
            </div>
          </TabsContent>
          <TabsContent value="type" className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="typed-name">Full legal name</Label>
              <Input id="typed-name" value={typedName} onChange={(e) => setTypedName(e.target.value)} placeholder="Jane Doe" />
            </div>
            <div className="space-y-1.5">
              <Label>Style</Label>
              <div className="grid grid-cols-3 gap-2">
                {TYPED_FONTS.map((f) => (
                  <button
                    key={f.id}
                    type="button"
                    onClick={() => setTypedFont(f.id)}
                    className={`border rounded-md p-3 text-center hover:bg-muted/50 ${typedFont === f.id ? "ring-2 ring-primary border-primary" : ""}`}
                  >
                    <span style={{ fontFamily: f.css, fontSize: 22 }}>{typedName.trim() || "Signature"}</span>
                    <div className="text-xs text-muted-foreground mt-1">{f.label}</div>
                  </button>
                ))}
              </div>
            </div>
          </TabsContent>
        </Tabs>

        <div className="flex items-start gap-2 pt-2">
          <Checkbox id="esign-consent" checked={consent} onCheckedChange={(v) => setConsent(v === true)} />
          <Label htmlFor="esign-consent" className="text-sm font-normal cursor-pointer">
            I agree that my electronic signature is the legal equivalent of my handwritten signature and that signing is binding.
          </Label>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={submitting}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {submitting ? "Signing…" : "Apply signature"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
